/*
 * Client-side prescription file checks.
 *
 * Because prescription files are uploaded straight to S3 via a presigned POST
 * (the actions never see the bytes), content checks have to happen here in the
 * browser before the upload. The `save` action re-verifies server-side, since
 * anything here is bypassable.
 */

// Bytes for the ASCII token "/Encrypt".
const ENCRYPT_TOKEN = [0x2f, 0x45, 0x6e, 0x63, 0x72, 0x79, 0x70, 0x74];

/** Find a byte sub-sequence within a Uint8Array. */
function indexOfBytes(haystack, needle) {
  outer: for (let i = 0; i <= haystack.length - needle.length; i++) {
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

/**
 * Detect a password-protected / encrypted PDF.
 *
 * A standard-security PDF references an unencrypted `/Encrypt` entry from its
 * trailer dictionary, so scanning the raw bytes for that token reliably flags
 * protected files without a PDF-parsing dependency. Non-PDFs return false.
 *
 * @param {File} file
 * @returns {Promise<boolean>}
 */
export async function isEncryptedPdf(file) {
  if (!file || file.type !== 'application/pdf') {
    return false;
  }
  const bytes = new Uint8Array(await file.arrayBuffer());
  return indexOfBytes(bytes, ENCRYPT_TOKEN) !== -1;
}

/**
 * Compute the lowercase hex SHA-256 of a file using the Web Crypto API.
 * Used to detect duplicate uploads for the same customer.
 *
 * @param {File} file
 * @returns {Promise<string>}
 */
export async function sha256Hex(file) {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
